import {Injectable} from '@angular/core';
import {catchError, defer, forkJoin, map, Observable, of, retry, switchMap, throwError, timer} from 'rxjs';

import {ProfiloCittadinoService, UserProfileDTO} from '../core/api/generated';
import {skipForbiddenRedirect} from '../interceptors/forbidden-redirect.context';
import {JwtIdentity, IdentityProfile} from '../shared/interfaces/jwt-identity.interface';
import {deveSaltareProfiloCittadino} from '../shared/utils/ruoli-token.util';
import {LocalStorageService} from './local-storage.service';
import {LogoutManagerService} from './logout-manager.service';

/**
 * Un 403 sul GET profilo iniziale significa che il token è valido ma l'utente non è
 * autorizzato ad accedere: non è un errore tecnico ritentabile, quindi la sessione va
 * chiusa e si torna al login invece di proseguire. Il logout è eseguito da
 * {@link UserSessionService.refreshUserData$}; `isProfileForbidden()` serve ai chiamanti
 * per riconoscere il caso e mostrare il proprio feedback (errore inline, toast).
 */
export function isProfileForbidden(err: unknown): boolean {
  return (err as {status?: number} | null | undefined)?.status === 403;
}

/**
 * Il BE risponde 409 `collegamento_account_richiesto` a qualunque chiamata autenticata con il
 * token di un'identità SPID/CIE il cui codice fiscale è già associato a un account con email e
 * password: l'identità temporanea non va trattata come sessione ma come richiesta di collegamento.
 * La classificazione è QUI e in un solo punto; il flusso di collegamento è di
 * `CollegamentoAccountService`. Nessun teardown: il token serve per le chiamate pubbliche
 * di collegamento.
 */
export const COLLEGAMENTO_ACCOUNT_RICHIESTO_KEY = 'collegamento_account_richiesto';

export class CollegamentoRichiestoError extends Error {
  readonly status = 409;

  constructor(readonly causa?: unknown) {
    super(COLLEGAMENTO_ACCOUNT_RICHIESTO_KEY);
    this.name = 'CollegamentoRichiestoError';
  }
}

export function isCollegamentoRichiesto(err: unknown): err is CollegamentoRichiestoError {
  return err instanceof CollegamentoRichiestoError;
}

function isCollegamentoRichiestoResponse(err: unknown): boolean {
  const e = err as {status?: number; error?: {key?: string} | null} | null | undefined;
  return e?.status === 409 && e?.error?.key === COLLEGAMENTO_ACCOUNT_RICHIESTO_KEY;
}

@Injectable({
  providedIn: 'root',
})
export class UserSessionService {
  constructor(
    private localStorageService: LocalStorageService,
    private profiloCittadinoService: ProfiloCittadinoService,
    private logoutManagerService: LogoutManagerService,
  ) {}

  private buildIdentityProfile(identity: JwtIdentity): IdentityProfile {
    return {
      id: identity.id,
      email: identity.email,
      nome: identity.nome,
      cognome: identity.cognome,
      fullname: identity.fullname,
    };
  }

  /**
   * Carica il profilo del cittadino (GET `/api/v1/cittadino/profilo`) e allinea la cache locale.
   *
   * Sul 403 chiude la sessione qui, una volta per tutte
   * ({@link LogoutManagerService.forceLogoutAccessDenied$}: stop del monitor token, pulizia
   * dello storage, rientro al login), poi ripropaga l'errore originale. I chiamanti NON devono
   * rifare il logout: riconoscono il caso con {@link isProfileForbidden} e si limitano al
   * proprio feedback utente.
   *
   * **Operatori di backoffice**: se i ruoli nel token dicono che l'utente non è un cittadino ma
   * un operatore ammesso in questa app ({@link deveSaltareProfiloCittadino}), la GET viene
   * **saltata** — il matcher BE la negherebbe con certezza e il 403 conseguente lo slogherebbe.
   * Non è un allentamento della difesa: il logout su 403 resta invariato per tutti i casi che
   * la chiamata la fanno ancora, cioè cittadini e ruoli non riconosciuti.
   */
  refreshUserData$(): Observable<{profile: IdentityProfile; userProfile: UserProfileDTO | null}> {
    const identity = this.localStorageService.decodeClaims() ?? {roles: []};
    const profile = this.buildIdentityProfile(identity);

    // Operatore di backoffice (SUPERADMIN / OP_COMUNE / OP_PRATICA): il profilo cittadino non
    // esiste per lui e il matcher BE è CITTADINO-only, quindi la GET darebbe un 403 certo — che
    // la difesa a valle interpreterebbe come "accesso negato all'app" e lo slogherebbe subito
    // dopo un login riuscito. Si salta la chiamata invece di indebolire il logout: la sessione
    // resta viva con la sola identità dal token e `userProfile` a null (assenza propagata, non
    // un profilo finto). Il 403 IMPREVISTO — ruolo non riconosciuto o nessun ruolo — non passa
    // da qui e continua a chiudere la sessione come prima.
    if (deveSaltareProfiloCittadino(identity.roles)) {
      return this.persistUserData$(profile, null);
    }

    return defer(() =>
      // Il 403 qui è un "non autorizzato ad accedere" gestito dai chiamanti con pulizia
      // storage + rientro al login: il redirect generico a /pages/home dell'interceptor
      // manderebbe l'utente dentro l'app con la sessione ancora attiva.
      this.profiloCittadinoService.getProfiloCittadino('body', false, {
        context: skipForbiddenRedirect(),
      }),
    ).pipe(
      map((dto) => dto as UserProfileDTO | null),
      catchError((err) => {
        // Prima di 401/403/404: un 409 collegamento_account_richiesto non è un errore tecnico né
        // un accesso negato, e non deve far scattare nessun teardown di sessione.
        if (isCollegamentoRichiestoResponse(err)) {
          return throwError(() => new CollegamentoRichiestoError(err));
        }
        if (err?.status === 404) {
          return of(null);
        }
        return throwError(() => err);
      }),
      retry({
        count: 1,
        delay: (err) => {
          // 401 (sessione scaduta) e 403 (utente non autorizzato) non sono transitori:
          // ritentarli produce solo una seconda risposta identica.
          // Idem il 409 di collegamento: la seconda risposta sarebbe identica.
          if (err?.status === 401 || isProfileForbidden(err) || isCollegamentoRichiesto(err)) {
            return throwError(() => err);
          }
          return timer(2000);
        },
      }),
      catchError((err) => {
        // 403 definitivo: il token è valido ma il BE nega l'accesso (nessun ruolo abilitato
        // per questa app). Il teardown della sessione è centralizzato qui così vale per ogni
        // chiamante, presente e futuro; l'errore viene comunque ripropagato perché ognuno
        // mostra un feedback diverso (errore inline sulla login, toast negli altri flussi).
        // Sta prima dello switchMap di scrittura: un 403 non deve toccare la cache.
        if (isProfileForbidden(err)) {
          return this.logoutManagerService
            .forceLogoutAccessDenied$()
            .pipe(switchMap(() => throwError(() => err)));
        }
        return throwError(() => err);
      }),
      switchMap((userProfile) => this.persistUserData$(profile, userProfile)),
    );
  }

  /**
   * Allinea la cache locale (identità dal JWT + profilo cittadino + anagrafiche) e restituisce
   * la coppia al chiamante. Condiviso fra il percorso cittadino e quello operatore così le due
   * strade lasciano lo storage nello stesso stato: per l'operatore `userProfile` è `null`,
   * esattamente come per il cittadino senza profilo ancora creato (404).
   */
  private persistUserData$(
    profile: IdentityProfile,
    userProfile: UserProfileDTO | null,
  ): Observable<{profile: IdentityProfile; userProfile: UserProfileDTO | null}> {
    return forkJoin([
      this.localStorageService.setPreferenceProfile$(profile),
      this.localStorageService.setUserProfile$(userProfile),
      this.localStorageService.setAnagrafiche$([]),
    ]).pipe(map(() => ({profile, userProfile})));
  }

  /**
   * Riallinea la cache `userProfile` col DTO appena restituito da un update
   * (`PUT /api/v1/cittadino/profilo`), senza un GET aggiuntivo. `UserSessionService`
   * resta lo scrittore (assieme all'onboarding) di `LocalStorageService.userProfile`:
   * la pagina Profilo NON scrive la cache, delega qui.
   */
  applyUserProfile$(userProfile: UserProfileDTO): Observable<UserProfileDTO | null> {
    return this.localStorageService.setUserProfile$(userProfile);
  }
}
